import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Siren, X, Camera, LockKey, CaretDown, CaretUp } from "@phosphor-icons/react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useRealtime } from "../lib/realtime";
import { inputCls, btnPrimary, btnGhost } from "./checklists/shared";
import { Overlay } from "./Overlay";

const ROOT_CAUSES = [["equipment_fault", "Equipment fault"], ["human_error", "Human error"], ["supplier_delivery", "Supplier / delivery"], ["power_outage", "Power outage"],
  ["staff_shortage", "Staff shortage"], ["cleaning_missed", "Cleaning missed"], ["calibration", "Calibration"], ["process_gap", "Process gap"], ["false_alarm", "False alarm"], ["other", "Other"]];

export function ResolveDialog({ exception, onClose, onResolved }) {
  const [note, setNote] = useState("");
  const [cause, setCause] = useState("equipment_fault");
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => setPhoto(r.result);
    r.readAsDataURL(f);
  };
  const submit = async () => {
    if (note.trim().length < 10) return toast.error("Describe the corrective action (at least 10 characters)");
    setBusy(true);
    try {
      const { data } = await api.post(`/exceptions/${exception.id}/resolve`, { note, root_cause: cause, photo_base64: photo });
      toast.success("Alarm cleared — resolution recorded in the audit trail");
      onResolved(data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Overlay onClose={onClose} z={1200} className="flex items-end sm:items-center justify-center sm:p-6">
      <div data-testid="resolve-dialog" onClick={(e) => e.stopPropagation()} className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl p-6 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] uppercase tracking-[0.15em] font-bold text-red-600">Clear critical alarm</p>
            <h2 className="font-heading text-lg font-bold mt-1">{exception.title}</h2>
            <p className="text-xs text-slate-500 mt-1">{exception.detail}</p>
            {exception.ai?.corrective_action && <p className="text-xs mt-2 bg-sky-50 border border-sky-100 text-sky-900 rounded-lg px-3 py-2"><span className="font-bold">AI suggests:</span> {exception.ai.corrective_action}</p>}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100"><X size={20} /></button>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-400">Root cause</label>
          <select data-testid="resolve-root-cause" value={cause} onChange={(e) => setCause(e.target.value)} className={`${inputCls} mt-1`}>
            {ROOT_CAUSES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-400">Corrective action taken (mandatory)</label>
          <textarea data-testid="resolve-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="What was done, by whom, and the re-check result…"
            className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:border-slate-900" />
        </div>
        <div className="flex items-center justify-between gap-2">
          <label className={`${btnGhost} flex items-center gap-1.5 cursor-pointer`}><Camera size={16} /> {photo ? "Photo attached" : "Add photo proof"}
            <input data-testid="resolve-photo" type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} /></label>
          <button data-testid="resolve-submit" disabled={busy} onClick={submit} className={btnPrimary.replace("bg-slate-900", "bg-red-600").replace("hover:bg-slate-800", "hover:bg-red-700")}>{busy ? "Recording…" : "Clear alarm"}</button>
        </div>
      </div>
    </Overlay>
  );
}

export function AlarmBanner() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [resolving, setResolving] = useState(null);
  const timer = useRef(null);
  const load = useCallback(() => {
    if (!user) return;
    api.get("/exceptions/active").then((r) => setItems(r.data.exceptions)).catch(() => {});
  }, [user]);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
  useRealtime((m) => {
    if (["exception.opened", "exception.resolved", "exception.escalated", "exception.updated"].includes(m.type)) {
      clearTimeout(timer.current); timer.current = setTimeout(load, 300);
    }
  });
  const visible = Boolean(user && items.length);
  const bannerRef = useRef(null);
  useEffect(() => {
    document.body.classList.toggle("has-alarm", visible);
    if (!visible || !bannerRef.current) { document.body.style.removeProperty("--alarm-h"); return undefined; }
    const el = bannerRef.current;
    const measure = () => document.body.style.setProperty("--alarm-h", `${el.offsetHeight}px`);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => { ro.disconnect(); document.body.classList.remove("has-alarm"); document.body.style.removeProperty("--alarm-h"); };
  }, [visible, open]);
  if (!visible) return null;
  const top = items[0];
  return (
    <>
      <div ref={bannerRef} data-testid="alarm-banner" role="alert" className="alarm-banner sticky top-0 z-[900] bg-red-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-2.5 flex items-center gap-3">
          <Siren size={22} weight="fill" className="shrink-0 animate-bounce" />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.2em] font-black opacity-90">{items.length} unresolved critical exception{items.length === 1 ? "" : "s"}</p>
            <p data-testid="alarm-banner-title" className="text-sm font-bold truncate">{top.title} <span className="font-semibold opacity-80">· {top.detail}</span></p>
          </div>
          {top.requires_senior && <span className="hidden sm:inline-flex items-center gap-1 text-[10px] font-black uppercase bg-white/20 px-2 py-1 rounded"><LockKey size={12} weight="fill" /> {top.senior_tier || "senior"} sign-off</span>}
          {top.can_resolve ? (
            <button data-testid="alarm-banner-clear" onClick={() => setResolving(top)} className="h-9 px-4 rounded-lg bg-white text-red-700 text-xs font-black tap-press whitespace-nowrap">Clear alarm</button>
          ) : <span className="text-[11px] font-bold opacity-90 whitespace-nowrap">Awaiting authorised sign-off</span>}
          {items.length > 1 && <button data-testid="alarm-banner-toggle" onClick={() => setOpen(!open)} className="p-1.5 rounded hover:bg-white/15">{open ? <CaretUp size={18} /> : <CaretDown size={18} />}</button>}
        </div>
        {open && (
          <div className="bg-red-700/95 border-t border-red-500">
            <div className="max-w-7xl mx-auto px-4 sm:px-8 py-2 divide-y divide-red-500/60">
              {items.slice(1).map((e) => (
                <div key={e.id} data-testid={`alarm-row-${e.id}`} className="py-2 flex items-center gap-3 text-sm">
                  <div className="flex-1 min-w-0"><p className="font-bold truncate">{e.title}</p><p className="text-xs opacity-80 truncate">{e.detail}</p></div>
                  {e.can_resolve && <button onClick={() => setResolving(e)} className="h-8 px-3 rounded-lg bg-white text-red-700 text-xs font-black">Clear</button>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      {resolving && <ResolveDialog exception={resolving} onClose={() => setResolving(null)} onResolved={() => { setResolving(null); load(); }} />}
    </>
  );
}
